// =============================================================================
// domain/disciplineSignals.ts — tilt, and what you keep doing wrong.
// -----------------------------------------------------------------------------
//   tiltSignal          does your win rate fall after a loss?
//   mistakeClustering   which tagged mistake shows up most?
//
// ── 🚨 TILT IS A SEQUENCE MEASURE, AND THE ORDER IS THE MEASUREMENT ──
//
// It asks how the NEXT trade goes after a win versus after a loss, so it needs
// the trades in the order they happened. Any other order and the number is
// arithmetic performed on a shuffled deck — still a number, still plausible.
//
// ⚠ A SCRATCH DOES NOT BREAK THE CHAIN. A flat trade between a loss and the next
// one is not a reset; the trader is still trading after that loss. It is skipped
// without becoming the new "previous outcome", which is the old client's rule and
// the right one.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

/** Fewer than this on either side and the comparison is noise. */
export const TILT_MIN_SAMPLE = 3;
/** Percentage points of win-rate difference before it is called a signal. */
export const TILT_THRESHOLD = 12;

export interface SequencedTrade {
  /** The EFFECTIVE date — resolved by the caller. */
  date: string | null;
  timeIn: string | null;
  netPnl: number | null;
}

export type TiltVerdict = 'tilt' | 'tightens-up' | 'steady' | 'not-enough';

export interface TiltSignal {
  /** Win rate on the trade AFTER a win, 0–100. Null below the sample floor. */
  afterWin: number | null;
  afterLoss: number | null;
  afterWinSample: number;
  afterLossSample: number;
  /** afterWin − afterLoss, in percentage points. Null if either side is null. */
  drop: number | null;
  verdict: TiltVerdict;
}

/**
 * How the next trade goes, after a win and after a loss.
 *
 * ⚠ THE POPULATION IS "TRADES THAT FOLLOWED A DECIDED TRADE", which is smaller
 * than the book by at least one and by however many scratches opened it. Both
 * counts are returned because a 22-point drop over four trades is not a finding.
 */
export function tiltSignal(trades: readonly SequencedTrade[]): TiltSignal {
  const seq = [...trades].sort((a, b) => (
    `${a.date ?? ''} ${a.timeIn ?? ''}`.localeCompare(`${b.date ?? ''} ${b.timeIn ?? ''}`)
  ));

  let winN = 0; let winW = 0;
  let lossN = 0; let lossW = 0;
  let prev: 'w' | 'l' | null = null;

  for (const t of seq) {
    const p = Number(t.netPnl) || 0;
    const o: 'w' | 'l' | null = p > 0 ? 'w' : p < 0 ? 'l' : null;
    // ⚠ A scratch is skipped WITHOUT updating `prev`. It is not a reset — the
    // trader is still trading after that loss.
    if (o == null) continue;
    if (prev === 'w') { winN += 1; if (o === 'w') winW += 1; }
    else if (prev === 'l') { lossN += 1; if (o === 'w') lossW += 1; }
    prev = o;
  }

  const enough = winN >= TILT_MIN_SAMPLE && lossN >= TILT_MIN_SAMPLE;
  const afterWin = enough ? (100 * winW) / winN : null;
  const afterLoss = enough ? (100 * lossW) / lossN : null;
  const drop = afterWin != null && afterLoss != null ? afterWin - afterLoss : null;

  return {
    afterWin, afterLoss,
    afterWinSample: winN, afterLossSample: lossN,
    drop,
    verdict: drop == null ? 'not-enough'
      : drop >= TILT_THRESHOLD ? 'tilt'
        : drop <= -TILT_THRESHOLD ? 'tightens-up' : 'steady',
  };
}

// ---------------------------------------------------------------------------
// Mistake clustering
// ---------------------------------------------------------------------------

/** The most a ranked list can usefully show before it stops being a ranking. */
export const MISTAKE_TOP_N = 7;

export interface MistakeTrade {
  booleans: Record<string, unknown> | null | undefined;
  mistakeTags: readonly string[] | null | undefined;
}

export interface MistakeRow {
  label: string;
  /** ⚠ TRADES, not occurrences — see `mistakeClustering`. */
  trades: number;
}

/**
 * The three discipline flags carry names of their own; a free-form tag saying
 * the same thing has to land on the same row or the ranking splits in two.
 */
const ALIASES: Record<string, string> = {
  revenge: 'Revenge',
  'revenge trade': 'Revenge',
  fomo: 'FOMO',
  overtraded: 'Overtraded',
  chased: 'Chased entry',
  'chased entry': 'Chased entry',
  'no confirmation': 'No confirmation',
  'no-confirmation': 'No confirmation',
  'moved stop': 'Moved stop',
  'moved-stop': 'Moved stop',
  'no stop': 'No stop',
  'no-stop': 'No stop',
};

export function normaliseMistake(raw: string): string | null {
  const k = String(raw ?? '').trim().toLowerCase();
  if (!k) return null;
  return ALIASES[k] ?? (k.charAt(0).toUpperCase() + k.slice(1));
}

/**
 * How often each tagged mistake appears, ranked.
 *
 * ⚠ DEDUPED PER TRADE. A trade with the `revengeTrade` flag AND a free-form
 * "revenge" tag is ONE revenge trade, not two — otherwise the trader who
 * annotates most thoroughly appears to make the most mistakes, which is exactly
 * backwards and would punish the habit the product is trying to build.
 */
export function mistakeClustering(trades: readonly MistakeTrade[]): MistakeRow[] {
  const tally = new Map<string, number>();

  for (const t of trades) {
    const seen = new Set<string>();
    const b = t.booleans ?? {};
    if (b['revengeTrade'] === true) seen.add('Revenge');
    if (b['fomoTrade'] === true) seen.add('FOMO');
    if (b['overtraded'] === true) seen.add('Overtraded');
    for (const tag of t.mistakeTags ?? []) {
      const n = normaliseMistake(tag);
      if (n) seen.add(n);
    }
    for (const m of seen) tally.set(m, (tally.get(m) ?? 0) + 1);
  }

  return [...tally.entries()]
    .map(([label, n]) => ({ label, trades: n }))
    // ⚠ Ties broken by NAME, so the order does not shuffle between renders of
    // the same data — an unstable ranking reads as the book having changed.
    .sort((a, b) => b.trades - a.trades || a.label.localeCompare(b.label))
    .slice(0, MISTAKE_TOP_N);
}
